import React, { useEffect, useState, useCallback } from "react";
import { Sentinel } from "../../model_gen/sentinel";
import { SentinelService } from "../../services/SentinelService";
import {
  Col,
  notification,
  Popover,
  Row,
  Table,
  Tag,
  Typography,
  Empty,
  Tooltip,
  Button,
} from "antd";
import moment from "moment/moment";
import {
  DeleteOutlined,
  PlusOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { usePermissions } from "../../PermissionsProvider";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { useNavigate } from "react-router-dom";
import { SentinelListProps, SeverityLevel } from "../../types/ISentinels";
import { PrimaryButton } from "../shared/button/Buttons";

const { Title } = Typography;

type SentinelColumns = ColumnsType<Sentinel>;

const SentinelList: React.FC<SentinelListProps> = ({
  intentGroupId,
  emptyText,
  excludeHeader = false,
  onSentinelClick,
}) => {
  const { apiKey } = usePermissions();

  const [sentinels, setSentinels] = useState<Sentinel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize] = useState(5);

  // Styles similar to IntentGroupOverview
  const styles = {
    contentCard: {
      background: "#fff",
      padding: "24px",
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      border: "1px solid #f0f0f0",
    },
    tableContainer: {
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
    },
    tagStyle: {
      borderRadius: "4px", 
      fontSize: "12px", 
      padding: "4px 10px"
    }
  };

  // Add global CSS for interactive rows
  useEffect(() => {
    // Add styles for interactive rows
    const style = document.createElement('style');
    style.innerHTML = `
      .sentinel-interactive-row:hover td {
        background-color: #f5f8ff !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Cache key based on current filters
  const getCacheKey = useCallback(() => {
    return `sentinels_${intentGroupId || ""}_${currentPage}_${pageSize}`;
  }, [intentGroupId, currentPage, pageSize]);

  const severityColorMap: Record<SeverityLevel, string> = {
    [SeverityLevel.UNRECOGNIZED]: "default",
    [SeverityLevel.INFO]: "default",
    [SeverityLevel.WARN]: "warning",
    [SeverityLevel.ERROR]: "warning",
    [SeverityLevel.FATAL]: "error",
  };

  const severityPopovers: Record<SeverityLevel, string> = {
    [SeverityLevel.UNRECOGNIZED]: "This Sentinel is NOT evaluating requests.",
    [SeverityLevel.INFO]: "This Sentinel is NOT evaluating requests.",
    [SeverityLevel.WARN]: "This Sentinel is NOT evaluating requests.",
    [SeverityLevel.ERROR]: "This Sentinel is NOT evaluating requests.",
    [SeverityLevel.FATAL]: "This Sentinel is evaluating requests.",
  };

  const navigate = useNavigate();

  const getSentinels = useCallback(() => {
    const cacheKey = getCacheKey();
    const cachedData = sessionStorage.getItem(cacheKey);

    if (cachedData) {
      const { data, totalCount } = JSON.parse(cachedData);
      setSentinels(data);
      setTotal(totalCount);
      setIsLoading(false);
      return;
    }

    if (intentGroupId) {
      SentinelService.getSentinelsByIntentGroup(
        intentGroupId,
        setIsLoading,
        apiKey,
        currentPage,
        pageSize
      ).subscribe(
        (response: { data: Sentinel[]; total: number }) => {
          const sortedSentinels = response.data.sort((a, b) => b.severity - a.severity);
          setSentinels(sortedSentinels);
          setTotal(response.total);
          sessionStorage.setItem(cacheKey, JSON.stringify({
            data: sortedSentinels,
            totalCount: response.total
          }));
          setIsLoading(false);
        },
        (error: any) => {
          console.error(error);
          setIsLoading(false);
        }
      );
    }
  }, [intentGroupId, apiKey, currentPage, pageSize, getCacheKey]);

  // Clear cache when component unmounts
  useEffect(() => {
    return () => {
      const cacheKey = getCacheKey();
      sessionStorage.removeItem(cacheKey);
    };
  }, [getCacheKey]);

  const deleteSentinel = (sentinelId: number) => {
    SentinelService.deleteSentinel(sentinelId, setIsLoading, apiKey).subscribe(
      () => {
        // Remove the deleted sentinel from the list
        const updatedSentinels = sentinels.filter(
          (sent) => sent.id !== sentinelId,
        );
        setSentinels(updatedSentinels);
        notification.success({
          message: "Success",
          description: "Sentinel deleted successfully",
        });
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: "Failed to delete sentinel",
        });
      },
    );
  };

  const sentinelColumns: SentinelColumns = [
    {
      title: "Name",
      dataIndex: "sentinel_name",
      key: "sentinel_name",
      render: (text: string) => (
        <Typography.Text strong>{text}</Typography.Text>
      ),
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      render: (state) => (
        <Tag
          color={
            state === "ACTIVE"
              ? "success"
              : state === "DISABLED"
                ? "error"
                : "default"
          }
          style={styles.tagStyle}>
          {state}
        </Tag>
      ),
    },
    {
      title: "Severity",
      dataIndex: "severity",
      key: "severity",
      render: (severity: SeverityLevel) => (
        <Popover content={severityPopovers[severity]}>
          <Tag 
            color={severityColorMap[severity]}
            style={styles.tagStyle}>
            {SeverityLevel[severity]}
          </Tag>
        </Popover>
      ),
    },
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      render: (timestamp: number) => {
        const date = moment(timestamp);
        return (
          <Tooltip title={date.format("MMMM D, YYYY h:mm:ss A")}>
            <div>
              <div style={{ fontWeight: 500 }}>{date.format("MMM D, YYYY")}</div>
              <div style={{ color: "#8c8c8c", fontSize: "12px" }}>{date.format("h:mm A")}</div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "",
      key: "action",
      align: "right",
      render: (_, record) => {
        return (
          <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button 
              type="text" 
              className="details-button"
              icon={<EditOutlined style={{ marginRight: "4px" }} />}
              onClick={(e) => {
                e.stopPropagation();
                if (intentGroupId) {
                  navigate(`/intent-group/${intentGroupId}/sentinel/${record.id}`);
                } else {
                  navigate(`/application/${record.application_id}/sentinel/${record.id}`);
                }
              }}
              style={{
                borderRadius: "6px",
                color: "#1890ff",
                display: "flex",
                alignItems: "center",
                padding: "0 10px",
                height: "32px",
                transition: "all 0.2s",
              }}
            >
              Edit
            </Button>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    if (intentGroupId) {
      getSentinels();
    }
  }, [intentGroupId, getSentinels]);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    if (pagination.current) {
      setCurrentPage(pagination.current);
    }
  };

  const handleRowClick = (record: Sentinel) => {
    if (onSentinelClick) {
      onSentinelClick(record.id);
    } else if (intentGroupId) {
      navigate(`/intent-group/${intentGroupId}/sentinel/${record.id}`);
    } else {
      navigate(`/application/${record.application_id}/sentinel/${record.id}`);
    }
  };

  return (
    <div style={excludeHeader ? {} : styles.contentCard}>
      {!excludeHeader && (
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={4} style={{ margin: 0, fontWeight: 600 }}>Sentinels</Title>
          </Col>
          <Col>
            <PrimaryButton
              icon={<PlusOutlined />}
              onClick={() => {
                if (intentGroupId) {
                  navigate(`/intent-group/${intentGroupId}/sentinel/new`);
                }
              }}>
              Add Sentinel
            </PrimaryButton>
          </Col>
        </Row>
      )}
      <div style={styles.tableContainer}>
        <Table
          dataSource={sentinels}
          columns={sentinelColumns}
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: false,
            showTotal: (total) => `Total ${total} items`
          }}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: "pointer" },
            className: "sentinel-interactive-row",
          })}
          locale={{
            emptyText: emptyText || (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <div>
                    <Typography.Text strong style={{ display: "block", marginBottom: "8px" }}>
                      No sentinels found
                    </Typography.Text>
                    <Typography.Text type="secondary">
                      Create a new sentinel to monitor and evaluate intents.
                    </Typography.Text>
                  </div>
                }
              />
            ),
          }}
          rowClassName={() => "sentinel-interactive-row"}
        />
      </div>
    </div>
  );
};

export default SentinelList;
